import { trackEvent } from '../utils/usage.js';
import { createStore } from 'vuex';
import { signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth';
import { settleSharedSignIn, markInteractiveSignIn, endSharedSignIn } from '@/utils/sharedSignIn';
import { auth, googleProvider, firebaseConfigured } from '@/firebase';
import { ensureSiteUserRecord, watchSiteUserStatus } from '@/store/siteAccess';
import {
  createRoom, joinRoom, leaveLobby, startRoom, bindRoom, submitPlacement,
  sweepMyRooms, setBotCount, seatOfUid, botDriver, markOver, recordWaiting,
} from '@/store/multiplayer';
import {
  foldGame, waitingMaps, drawerOfMap, mapDrawnBy, validatePlacement,
  MIN_PLAYERS, MAX_PLAYERS,
} from '@/engine/game';
import { choosePlacement, BOT_NAMES } from '@/engine/ai/heuristicBot';
import { outcomeOf, fingerprintOf, recordFinishedGame } from '@/store/userStats';
import { createRngState } from '@/engine/rng/rng';

const SAVE_KEY = 'cartographers:game';
const SETTINGS_KEY = 'cartographers:settings';

// One pending bot drawing at a time, the house rule - two armed chains
// double-draw. The decision is recomputed when the timer fires, never
// applied from when it was armed.
let botTimer = null;
const cancelBotTimer = () => {
  if (botTimer !== null) {
    window.clearTimeout(botTimer);
    botTimer = null;
  }
};

// A quick sketcher, not a deliberator: everyone draws at once in this game,
// so the pause is watchability, not turn-taking.
export const BOT_PAUSE_MS = 1100;
export const BOT_PAUSE_JITTER = 0.4;
const botPause = () => Math.round(BOT_PAUSE_MS * (1 + (Math.random() * 2 - 1) * BOT_PAUSE_JITTER));

// Online: how long a human may be gone before the driver draws for them.
// Same grace as the sibling games' takeover rule; the seat is theirs again
// the moment they reconnect.
export const TAKEOVER_GRACE_MS = 30 * 1000;
const lastSeenGone = new Map(); // uid -> when this client noticed them vanish

const loadJson = (key) => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const saveJson = (key, value) => {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // A full or disabled localStorage must never break play.
  }
};

// Deterministic bot RNG per drawing: replaying a room's placements replays
// the bots exactly, and two clients that ever both drove would compute the
// same drawing (the write-once placement node is the backstop anyway).
const botRng = (seed, turnIndex, mapSeat) => createRngState(
  (seed ^ (turnIndex * 1000003) ^ (mapSeat * 7919)) | 0,
);

let unbindRoomFn = null;
let unwatchStatusFn = null;

export default createStore({
  state: {
    firebaseConfigured,
    authReady: !firebaseConfigured,
    // Separate from authReady: Firebase Auth can answer before the
    // siteUsers watcher has, and the gate must not flash "waiting for
    // approval" at an approved player in that gap.
    siteUserReady: !firebaseConfigured,
    user: null,
    siteUser: null,

    // Local (offline) game: everything the fold needs, saved for resume.
    local: null, // { seed, players: [{ id, name, isBot }], placements }
    hasSavedGame: Boolean(loadJson(SAVE_KEY)),
    settings: loadJson(SETTINGS_KEY) || { playerName: 'You', opponents: 2 },

    // Online room.
    roomId: null,
    roomMeta: null,
    roomGame: null, // { seed }
    roomPlacements: {},
    presence: {},
    roomError: null,
  },

  getters: {
    isApproved: (state) => state.siteUser?.status === 'approved',
    isAdmin: (state) => Boolean(state.siteUser?.isAdmin),
    isHost: (state) => Boolean(state.roomMeta && state.user && state.roomMeta.hostUid === state.user.uid),
    myUid: (state) => state.user?.uid || null,
    isOnline: (state) => Boolean(state.roomId),
    inLobby: (state) => state.roomMeta?.status === 'lobby',

    seats: (state) => [...(state.roomMeta?.playerSlots || [])].sort((a, b) => a.seatIndex - b.seatIndex),
    humanSeats: (state, getters) => getters.seats.filter((s) => !s.isBot),
    botSeats: (state, getters) => getters.seats.filter((s) => s.isBot),
    enoughPlayers: (state, getters) => getters.seats.length >= MIN_PLAYERS,
    tableFull: (state, getters) => getters.seats.length >= MAX_PLAYERS,

    // The unified shape both modes reduce to.
    players: (state, getters) => {
      if (state.roomId) {
        return getters.seats.map((s, i) => ({
          id: s.uid, name: s.displayName, seat: i, isBot: Boolean(s.isBot),
        }));
      }
      return (state.local?.players || []).map((p, i) => ({ ...p, seat: i }));
    },
    seed: (state) => (state.roomId ? state.roomGame?.seed ?? null : state.local?.seed ?? null),
    placements: (state) => (state.roomId ? state.roomPlacements : state.local?.placements || {}),

    view: (state, getters) => {
      if (getters.seed === null || !getters.players.length) return null;
      return foldGame(getters.seed, getters.players.length, getters.placements);
    },

    mySeat: (state, getters) => {
      if (state.roomId) return seatOfUid(state.roomMeta?.playerSlots, state.user?.uid);
      return getters.players.findIndex((p) => !p.isBot);
    },

    // The map I am drawing on this turn - my own, or on an ambush turn the
    // neighbour's that just got passed to me (rules p7).
    myMapSeat: (state, getters) => {
      const { view, mySeat, players } = getters;
      if (!view || view.phase !== 'playing' || mySeat < 0) return mySeat;
      return mapDrawnBy(view.turn, mySeat, players.length);
    },

    iHavePlaced: (state, getters) => {
      const { view, myMapSeat } = getters;
      if (!view || view.phase !== 'playing') return true;
      return Boolean(getters.placements?.[view.turnIndex]?.[myMapSeat]);
    },

    // Names still sketching, for the "waiting on" line. Bots are always
    // "here"; the driver draws for them momentarily.
    stillDrawing: (state, getters) => {
      const { view, players } = getters;
      if (!view) return [];
      return waitingMaps(view, getters.placements)
        .map((m) => players[drawerOfMap(view.turn, m, players.length)])
        .filter(Boolean);
    },

    myDriverUid: (state, getters) => {
      if (!state.roomId) return getters.myUid || 'local';
      return botDriver(state.roomMeta?.playerSlots, state.presence);
    },
  },

  mutations: {
    setAuthReady: (s, v) => { s.authReady = v; },
    setUser: (s, v) => { s.user = v; },
    setSiteUser: (s, v) => { s.siteUser = v; s.siteUserReady = true; },
    setSiteUserReady: (s, v) => { s.siteUserReady = v; },

    setLocal(state, local) {
      state.local = local;
      state.hasSavedGame = Boolean(local);
      saveJson(SAVE_KEY, local);
    },
    recordLocalPlacement(state, { turnIndex, mapSeat, placement }) {
      const placements = { ...(state.local.placements || {}) };
      placements[turnIndex] = { ...(placements[turnIndex] || {}), [mapSeat]: JSON.stringify(placement) };
      state.local = { ...state.local, placements };
      saveJson(SAVE_KEY, state.local);
    },
    setSettings(state, settings) {
      state.settings = { ...state.settings, ...settings };
      saveJson(SETTINGS_KEY, state.settings);
    },

    setRoom: (s, { id, meta }) => { s.roomId = id; s.roomMeta = meta ?? null; },
    setRoomMeta: (s, v) => { s.roomMeta = v; },
    setRoomGame: (s, v) => { s.roomGame = v; },
    setRoomPlacements: (s, v) => { s.roomPlacements = v || {}; },
    setPresence: (s, v) => { s.presence = v || {}; },
    setRoomError: (s, v) => { s.roomError = v; },
  },

  actions: {
    // ---------------------------------------------------------------- auth
    initAuth({ commit }) {
      if (!firebaseConfigured || !auth) { commit('setAuthReady', true); return; }
      onAuthStateChanged(auth, async (user) => {
        if (await settleSharedSignIn(auth, user)) return; // see utils/sharedSignIn.js
        if (unwatchStatusFn) { unwatchStatusFn(); unwatchStatusFn = null; }
        // An anonymous session exists only so a signed-out bug report can
        // carry a token (utils/bugReports.js). It is NOT a player.
        if (user?.isAnonymous) {
          commit('setUser', null);
          commit('setSiteUser', null);
          commit('setAuthReady', true);
          return;
        }
        commit('setUser', user ? { uid: user.uid, displayName: user.displayName, email: user.email } : null);
        if (user) {
          commit('setSiteUserReady', false);
          await ensureSiteUserRecord(user);
          unwatchStatusFn = watchSiteUserStatus(user.uid, (su) => commit('setSiteUser', su));
        } else {
          commit('setSiteUser', null);
        }
        commit('setAuthReady', true);
      });
    },

    async signIn({ commit }) {
      if (!auth) return;
      try { markInteractiveSignIn(); await signInWithPopup(auth, googleProvider); } catch (e) { commit('setRoomError', e.message); }
    },

    async signOutOfSite({ commit, dispatch }) {
      await dispatch('leaveRoom');
      if (auth) { await endSharedSignIn(); await signOut(auth); }
      commit('setUser', null);
      commit('setSiteUser', null);
    },

    // ---------------------------------------------------------- local play
    newLocalGame({ commit, state, dispatch }, { playerName, opponents } = {}) {
      const name = playerName || state.settings.playerName || 'You';
      const botCount = Math.max(
        MIN_PLAYERS - 1,
        Math.min(opponents ?? state.settings.opponents ?? 2, MAX_PLAYERS - 1),
      );
      cancelBotTimer();
      commit('setSettings', { playerName: name, opponents: botCount });
      commit('setLocal', {
        seed: Date.now() | 0,
        players: [
          { id: 'you', name, isBot: false },
          ...Array.from({ length: botCount }, (_, i) => ({
            id: `bot${i}`, name: BOT_NAMES[i % BOT_NAMES.length], isBot: true,
          })),
        ],
        placements: {},
      });
      dispatch('runBots');
    },

    resumeGame({ commit, dispatch }) {
      const saved = loadJson(SAVE_KEY);
      if (!saved) return;
      cancelBotTimer();
      commit('setLocal', saved);
      dispatch('runBots');
    },

    quitLocalGame({ commit }) {
      cancelBotTimer();
      commit('setLocal', null);
    },

    // ------------------------------------------------------------- drawing
    /**
     * The human's drawing, both modes. Validated against the fold before it
     * goes anywhere, so a stale click cannot write an illegal record.
     */
    async place({ commit, state, getters, dispatch }, placement) {
      const { view, myMapSeat } = getters;
      if (!view || view.phase !== 'playing' || getters.iHavePlaced || myMapSeat < 0) return;
      const grid = view.maps[myMapSeat].grid;
      if (!validatePlacement(grid, view.turn, placement)) {
        commit('setRoomError', 'That placement is not legal.');
        return;
      }
      if (state.roomId) {
        try {
          await submitPlacement({
            gameId: state.roomId, turnIndex: view.turnIndex, mapSeat: myMapSeat, placement,
          });
          recordWaiting({ gameId: state.roomId, view: getters.view, players: getters.players, placements: getters.placements, meta: state.roomMeta });
        } catch (e) { commit('setRoomError', e.message); }
      } else {
        commit('recordLocalPlacement', { turnIndex: view.turnIndex, mapSeat: myMapSeat, placement });
      }
      dispatch('runBots');
    },

    /**
     * Draws for every map owed by a bot - and, online, by a human who has
     * been gone past the grace. Only the driver does this; the write-once
     * placement node is the backstop if two clients ever both think they
     * drive. One timer, decision recomputed when it fires.
     */
    runBots({ state, getters, dispatch }) {
      if (botTimer !== null) return;
      const { view, players } = getters;
      if (!view || view.phase !== 'playing') { dispatch('maybeFinishRoom'); return; }
      if (state.roomId && getters.myDriverUid !== state.user?.uid) return;

      const owed = waitingMaps(view, getters.placements).filter((m) => {
        const drawer = players[drawerOfMap(view.turn, m, players.length)];
        if (!drawer) return false;
        if (drawer.isBot) return true;
        if (!state.roomId) return false;
        if (state.presence[drawer.id]) { lastSeenGone.delete(drawer.id); return false; }
        const gone = lastSeenGone.get(drawer.id);
        if (!gone) { lastSeenGone.set(drawer.id, Date.now()); return false; }
        return Date.now() - gone > TAKEOVER_GRACE_MS;
      });
      if (!owed.length) {
        // Somebody may still be inside the grace window - check back.
        if (state.roomId && getters.stillDrawing.some((p) => !p.isBot && !state.presence[p.id])) {
          botTimer = window.setTimeout(() => { botTimer = null; dispatch('runBots'); }, 5000);
        }
        return;
      }

      botTimer = window.setTimeout(async () => {
        botTimer = null;
        // Recompute everything: the turn may have moved while this was armed.
        const now = getters.view;
        if (!now || now.phase !== 'playing') { dispatch('maybeFinishRoom'); return; }
        if (state.roomId && getters.myDriverUid !== state.user?.uid) return;
        const stillOwed = waitingMaps(now, getters.placements).filter((m) => {
          const drawer = getters.players[drawerOfMap(now.turn, m, getters.players.length)];
          return drawer && (drawer.isBot
            || (state.roomId && !state.presence[drawer.id]
              && Date.now() - (lastSeenGone.get(drawer.id) || Date.now()) > TAKEOVER_GRACE_MS));
        });
        if (!stillOwed.length) { dispatch('runBots'); return; }

        const mapSeat = stillOwed[0];
        const { placement } = choosePlacement({
          grid: now.maps[mapSeat].grid,
          turn: now.turn,
          script: now.script,
          rng: botRng(getters.seed, now.turnIndex, mapSeat),
          forOwnMap: now.turn.type !== 'ambush',
        });
        if (placement) {
          if (state.roomId) {
            await submitPlacement({
              gameId: state.roomId, turnIndex: now.turnIndex, mapSeat, placement,
            }).catch(() => {});
            recordWaiting({ gameId: state.roomId, view: getters.view, players: getters.players, placements: getters.placements, meta: state.roomMeta });
          } else {
            dispatch('recordBotPlacement', { turnIndex: now.turnIndex, mapSeat, placement });
          }
        }
        dispatch('runBots');
      }, botPause());
    },

    recordBotPlacement({ commit }, payload) {
      commit('recordLocalPlacement', payload);
    },

    // Lifetime stats, once per finished game. Safe to dispatch as often as
    // anything notices the fold has ended - the fingerprint transaction in
    // userStats.js is what makes it exactly-once, not the caller.
    recordMyStats({ state, getters }) {
      const outcome = outcomeOf({
        view: getters.view,
        mySeat: getters.mySeat,
        online: Boolean(state.roomId),
      });
      if (!outcome) return;
      recordFinishedGame({
        uid: getters.myUid,
        name: state.user?.displayName || state.user?.email || null,
        fingerprint: fingerprintOf({ seed: getters.seed, view: getters.view }),
        outcome,
      });
    },

    // The room's status feeds the sweeper's age limits; the driver stamps
    // 'over' when the fold says the game is.
    maybeFinishRoom({ state, getters }) {
      if (!state.roomId || state.roomMeta?.status !== 'playing') return;
      if (getters.view?.phase !== 'over') return;
      if (getters.myDriverUid !== state.user?.uid) return;
      markOver({ gameId: state.roomId });
    },

    // -------------------------------------------------------------- rooms
    sweepRooms({ state }) {
      if (!state.user) return;
      sweepMyRooms({ user: state.user }).catch(() => {});
    },

    async hostRoom({ commit, state, dispatch }, { async: asyncRoom = false } = {}) {
      trackEvent('room-hosted');
      commit('setRoomError', null);
      try {
        const { gameId } = await createRoom({ user: state.user, async: asyncRoom });
        dispatch('enterRoom', gameId);
      } catch (e) { commit('setRoomError', e.message); }
    },

    async joinRoomByCode({ commit, state, dispatch }, code) {
      trackEvent('room-joined');
      commit('setRoomError', null);
      try {
        const { gameId } = await joinRoom({ user: state.user, code });
        dispatch('enterRoom', gameId);
      } catch (e) { commit('setRoomError', e.message); }
    },

    enterRoom({ commit, state, dispatch }, gameId) {
      if (unbindRoomFn) { unbindRoomFn(); unbindRoomFn = null; }
      cancelBotTimer();
      lastSeenGone.clear();
      commit('setRoom', { id: gameId, meta: null });
      commit('setRoomGame', null);
      commit('setRoomPlacements', {});

      unbindRoomFn = bindRoom({
        gameId,
        user: state.user,
        onMeta: (meta) => { commit('setRoomMeta', meta); if (meta === null) dispatch('leaveRoom'); },
        onGame: (game) => { commit('setRoomGame', game); dispatch('runBots'); },
        onPlacements: (placements) => { commit('setRoomPlacements', placements); dispatch('runBots'); },
        onPresence: (presence) => {
          commit('setPresence', presence);
          // Presence decides who drives, so a change can make this client
          // newly responsible for the bots.
          dispatch('runBots');
        },
      });
    },

    async leaveRoom({ commit, state }) {
      if (unbindRoomFn) { unbindRoomFn(); unbindRoomFn = null; }
      cancelBotTimer();
      lastSeenGone.clear();
      if (state.roomId && state.user) {
        try { await leaveLobby({ user: state.user, gameId: state.roomId }); } catch { /* best effort */ }
      }
      commit('setRoom', { id: null, meta: null });
      commit('setRoomGame', null);
      commit('setRoomPlacements', {});
      commit('setPresence', {});
    },

    async changeBotCount({ commit, state }, count) {
      try {
        await setBotCount({
          user: state.user, gameId: state.roomId, meta: state.roomMeta, count,
        });
      } catch (e) { commit('setRoomError', e.message); }
    },

    async deal({ commit, state }) {
      commit('setRoomError', null);
      try {
        await startRoom({ user: state.user, gameId: state.roomId, meta: state.roomMeta });
      } catch (e) { commit('setRoomError', e.message); }
    },
  },
});
